import { useCallback, useEffect, useState } from 'react'

import { useDebounce } from '@/hooks/useDebounce'

import { AnnotationMetadata } from '../types'

interface UseStableSelectionOptions {
  enabled: boolean
  delay?: number
  shadowRoot: ShadowRoot | null
  onStableSelection: (metadata: AnnotationMetadata | null) => void
}

interface UseStableSelectionResult {
  currentSelection: AnnotationMetadata | null
  isStable: boolean
}

export const useStableSelection = ({
  enabled,
  delay = 400,
  shadowRoot,
  onStableSelection,
}: UseStableSelectionOptions): UseStableSelectionResult => {
  const [rawSelection, setRawSelection] = useState<AnnotationMetadata | null>(
    null
  )

  const debouncedSelection = useDebounce(rawSelection, delay)

  const handleAnnotationSelection = useCallback((event: Event) => {
    const customEvent = event as CustomEvent<{ metadata: AnnotationMetadata }>
    const metadata = customEvent.detail.metadata

    setRawSelection(metadata)
  }, [])

  useEffect(() => {
    onStableSelection(debouncedSelection)
  }, [debouncedSelection, onStableSelection])

  useEffect(() => {
    if (!enabled || !shadowRoot) {
      return
    }

    const shadowHost = shadowRoot.host

    shadowHost.addEventListener(
      'annotation-selection',
      handleAnnotationSelection
    )

    return () => {
      shadowHost.removeEventListener(
        'annotation-selection',
        handleAnnotationSelection
      )
    }
  }, [enabled, shadowRoot, handleAnnotationSelection])

  const isStable = rawSelection === debouncedSelection

  return {
    currentSelection: rawSelection,
    isStable,
  }
}
